import { TFunction } from 'next-i18next';
import yup from '@services/translation/translatedYup';

const validateActualFile = (
  value: any,
  validationFn: (file: File) => boolean
): boolean => {
  // Allow boolean true (when certificate already exists)
  if (value === true) return true;
  if (!value || !(value instanceof File)) return false;
  return validationFn(value);
};

export const validateFile = (
  value: File | boolean,
  t: TFunction,
  maxSizeInMB: number = 10
): string | null => {
  try {
    createFileValidationSchema(t, maxSizeInMB).validateSync(value);
    return null;
  } catch (error: any) {
    return error.message;
  }
};

export const createFileValidationSchema = (
  t: TFunction,
  maxSizeInMB: number = 10
) => yup
  .mixed()
  .required(t('certificateValidation_fileRequired.message'))
  .test('file-type', t('certificateValidation_fileType.message'), (value) => validateActualFile(value, (file) => {
    const acceptedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    return acceptedTypes.includes(file.type);
  }))
  .test(
    'file-size',
    t('certificateValidation_fileSize.message', { size: maxSizeInMB }),
    (value) => validateActualFile(value, (file) => {
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      return file.size <= maxSizeInBytes && file.size > 0;
    })
  )
  .test(
    'file-extension',
    t('certificateValidation_fileExtension.message'),
    (value) => validateActualFile(value, (file) => {
      const allowedExtensions = [
        '.jpg',
        '.jpeg',
        '.png',
        '.pdf',
      ];
      const fileName = file.name.toLowerCase();
      return allowedExtensions.some((ext) => fileName.endsWith(ext));
    })
  )
  .test(
    'file-name-safe',
    t('certificateValidation_fileNameInvalid.message'),
    (value) => validateActualFile(value, (file) => {
      if (file.name.length > 255) return false;
      // eslint-disable-next-line no-control-regex
      const dangerousChars = /[<>:"/\\|?*]|[\u0000-\u001f]/;
      return !dangerousChars.test(file.name);
    })
  );
